import clsx from 'clsx';

import { Card, Badge, Icon } from '@lib/ui';
import type { PersonaArchetype } from '../types';
import type { ColorType } from '@lib/types';
import type { IconName } from '@lib/ui';

export type ArchetypeCardProps = {
  archetype: PersonaArchetype;
  selected?: boolean;
  onClick?: () => void;
  showPreview?: boolean;
  className?: string;
};

function ArchetypeCard({
  archetype,
  selected,
  onClick,
  showPreview = false,
  className,
}: ArchetypeCardProps) {
  const color = archetype.color as ColorType;
  const iconName = (archetype.icon || 'lightbulb') as IconName;

  return (
    <Card
      skin={selected ? 'bordered' : 'shadow'}
      className={clsx(
        'p-4 cursor-pointer transition-all duration-200',
        selected && [
          'ring-2 ring-primary-500 dark:ring-primary-400',
          'border-primary-500 dark:border-primary-400',
        ],
        !selected && 'hover:shadow-lg hover:border-gray-300 dark:hover:border-dark-500',
        className
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={clsx(
            'shrink-0 p-2 rounded-lg',
            `bg-${color}-100 text-${color}-600`,
            `dark:bg-${color}-900/30 dark:text-${color}-400`
          )}
          style={{
            backgroundColor: `var(--color-${color}-100)`,
            color: `var(--color-${color}-600)`,
          }}
        >
          <Icon name={iconName} size="lg" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {archetype.name}
            </h3>
            {selected && (
              <Badge variant="filled" color="primary" className="shrink-0">
                Selected
              </Badge>
            )}
          </div>

          {/* Description */}
          <p className="mt-1 text-sm text-gray-600 dark:text-dark-300 line-clamp-2">
            {archetype.description}
          </p>

          {/* Traits preview */}
          {archetype.persona.voice?.traits && (
            <div className="mt-2 flex flex-wrap gap-1">
              {archetype.persona.voice.traits.slice(0, 3).map((trait: string) => (
                <Badge key={trait} variant="soft" color="neutral" className="text-xs">
                  {trait}
                </Badge>
              ))}
            </div>
          )}

          {/* Example output preview */}
          {showPreview && (
            <div className="mt-3 p-2 bg-gray-50 dark:bg-dark-700 rounded text-xs text-gray-600 dark:text-dark-300 italic line-clamp-2">
              "{archetype.exampleOutput}"
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

ArchetypeCard.displayName = 'ArchetypeCard';

export { ArchetypeCard };
